export type StructuredTextParts = {
  main: string;
  items: string[];
};

function repairWrappedInlineCoordinates(value: string): string {
  return value.replace(/([（(\[][^)\]\n]{0,80}?),\s*\n\s*([^)\]\n]{0,80}?[)）\]])/g, '$1,$2');
}

function normalizeString(value: string): string {
  return repairWrappedInlineCoordinates(
    value.replace(/\\n/g, '\n').replace(/\r\n/g, '\n'),
  ).trim();
}

function stripBulletPrefix(value: string): string {
  return value.replace(/^(?:[-*•]|\d+[.)]|[（(]?[一二三四五六七八九十]+[）)])\s*/, '').trim();
}

function hasBulletPrefix(value: string): boolean {
  return /^(?:[-*•]|\d+[.)]|[（(]?[一二三四五六七八九十]+[）)])\s*/.test(value.trim());
}

function tryParseMaybeJson(value: string): unknown {
  const normalized = normalizeString(value).replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '').trim();
  if (!normalized) {
    return '';
  }

  const looksLikeJson = (
    (normalized.startsWith('{') && normalized.endsWith('}')) ||
    (normalized.startsWith('[') && normalized.endsWith(']'))
  );

  if (looksLikeJson) {
    try {
      return JSON.parse(normalized);
    } catch {
      return normalized;
    }
  }

  return normalized;
}

function unwrapStructuredRoot(value: unknown, mode?: 'question' | 'answer'): unknown {
  const parsed = typeof value === 'string' ? tryParseMaybeJson(value) : value;

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return parsed;
  }

  const record = parsed as Record<string, unknown>;

  if (mode === 'question' && record.Question) {
    return record.Question;
  }
  if (mode === 'answer' && record.Answer) {
    return record.Answer;
  }
  if (!mode && record.Question) {
    return record.Question;
  }
  if (!mode && record.Answer) {
    return record.Answer;
  }

  return record;
}

function extractFirstMeaningfulText(value: unknown, mode?: 'question' | 'answer'): string {
  const root = unwrapStructuredRoot(value, mode);

  if (!root) {
    return '';
  }
  if (typeof root === 'string') {
    return normalizeString(root);
  }
  if (typeof root === 'number' || typeof root === 'boolean') {
    return String(root);
  }
  if (Array.isArray(root)) {
    for (const item of root) {
      const next = extractFirstMeaningfulText(item, mode);
      if (next) {
        return next;
      }
    }
    return '';
  }

  const record = root as Record<string, unknown>;
  const preferredKeys = mode === 'answer'
    ? ['main', 'Main', 'subAnswer', 'sub_answer', 'answer', 'Answer', 'solution', 'explanation', 'text', 'value', 'content', 'title', 'subQuestion', 'subQustion']
    : ['main', 'Main', 'subQuestion', 'subQustion', 'sub_question', 'question', 'Question', 'stem', 'text', 'value', 'content', 'title', 'subAnswer', 'answer'];

  for (const key of preferredKeys) {
    const next = extractFirstMeaningfulText(record[key], mode);
    if (next) {
      return next;
    }
  }

  for (const child of Object.values(record)) {
    const next = extractFirstMeaningfulText(child, mode);
    if (next) {
      return next;
    }
  }

  return '';
}

export function extractStructuredTextParts(value: unknown, mode: 'question' | 'answer' = 'question'): StructuredTextParts {
  const root = unwrapStructuredRoot(value, mode);

  if (!root) {
    return { main: '', items: [] };
  }

  if (typeof root === 'string') {
    const lines = normalizeString(root)
      .split(/\n+/)
      .map((line) => line.trim())
      .filter(Boolean);

    if (!lines.length) {
      return { main: '', items: [] };
    }

    return {
      main: stripBulletPrefix(lines[0]),
      items: lines.slice(1).map(stripBulletPrefix).filter(Boolean),
    };
  }

  if (Array.isArray(root)) {
    const lines = root.map((item) => extractFirstMeaningfulText(item, mode)).map(normalizeString).filter(Boolean);
    return {
      main: lines[0] || '',
      items: lines.slice(1).map(stripBulletPrefix).filter(Boolean),
    };
  }

  const record = root as Record<string, unknown>;
  const mainSource = mode === 'answer'
    ? (record.main
      ?? record.Main
      ?? record.mainAnswer
      ?? record.main_answer
      ?? record.answer
      ?? record.Answer
      ?? record.solution
      ?? record.explanation
      ?? '')
    : (record.main
      ?? record.Main
      ?? record.mainQuestion
      ?? record.main_question
      ?? record.question
      ?? record.Question
      ?? record.stem
      ?? record);
  const main = normalizeString(extractFirstMeaningfulText(mainSource, mode));
  const source = mode === 'question'
    ? (record.subQuestions ?? record.sub_questions ?? record.questions ?? record.items)
    : (record.subAnswers ?? record.sub_answers ?? record.answers ?? record.subQuestions ?? record.sub_questions ?? record.questions ?? record.items);
  const hasStructuredItems = Array.isArray(source);

  const items = Array.isArray(source)
    ? source
      .map((item) => extractFirstMeaningfulText(
        typeof item === 'object' && item !== null
          ? (mode === 'answer'
            ? ((item as Record<string, unknown>).subAnswer
              ?? (item as Record<string, unknown>).sub_answer
              ?? (item as Record<string, unknown>).answer
              ?? (item as Record<string, unknown>).Answer
              ?? (item as Record<string, unknown>).solution
              ?? '')
            : ((item as Record<string, unknown>).subQuestion
              ?? (item as Record<string, unknown>).subQustion
              ?? (item as Record<string, unknown>).sub_question
              ?? (item as Record<string, unknown>).question
              ?? (item as Record<string, unknown>).Question
              ?? item))
          : item,
        mode
      ))
      .map(normalizeString)
      .map(stripBulletPrefix)
      .filter(Boolean)
    : [];

  if (!main && items.length && !hasStructuredItems) {
    return { main: items[0], items: items.slice(1) };
  }

  return { main, items };
}

export function hasExplicitStructuredMain(value: unknown, mode: 'question' | 'answer' = 'question'): boolean {
  const root = unwrapStructuredRoot(value, mode);

  if (!root) {
    return false;
  }

  if (typeof root === 'string') {
    const lines = normalizeString(root)
      .split(/\n+/)
      .map((line) => line.trim())
      .filter(Boolean);

    return Boolean(lines[0] && !hasBulletPrefix(lines[0]));
  }

  if (Array.isArray(root)) {
    return false;
  }

  if (typeof root !== 'object') {
    return false;
  }

  const record = root as Record<string, unknown>;
  const mainKeys = mode === 'question'
    ? ['main', 'Main', 'mainQuestion', 'main_question', 'questionStem', 'question_stem', 'stem', 'question', 'Question']
    : ['main', 'Main', 'mainAnswer', 'main_answer', 'answer', 'Answer', 'solution', 'explanation'];

  return mainKeys.some((key) => {
    if (!Object.prototype.hasOwnProperty.call(record, key)) {
      return false;
    }

    return Boolean(normalizeString(extractFirstMeaningfulText(record[key], mode)));
  });
}

export function safeExtractText(val: unknown): string {
  const structured = extractStructuredTextParts(val);
  return structured.main || structured.items[0] || '';
}
